import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { env } from "../../../../../env";
import axiosInstance from "../../../../globalInspector/axiosInstance";
import CommonToast from "../../../../../components/_utils/CommonToast";
import ICONS from "../../../../../assets/Icons";

const baseUrl = env.REACT_APP_BASE_URL;

export const getInvoices = createAsyncThunk(
  "getInvoices",
  async (values, { rejectWithValue }) => {
    const { page_no, page_size } = values;
    try {
      const response = await axiosInstance.get(`${baseUrl}/billing/invoices`, {
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": sessionStorage.getItem("q2p_token"),
        },
        params: {
          page_no: page_no,
          page_size: page_size,
        },
      });
      return response.data;
    } catch (error) {
      CommonToast({
        text: error.response.data.error,
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      return rejectWithValue(error);
    }
  },
);

export const downloadInvoice = createAsyncThunk(
  "downloadInvoice",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}/billing/${id}/download_invoice.pdf`,
        {},
        {
          responseType: "blob",
          validateStatus: () => true,
        },
      );

      if (response.status === 404) {
        return rejectWithValue(undefined);
      }

      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response.data);
      }

      const blob = new Blob([response.data], { type: "application/pdf" });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", `statement.pdf`);

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      CommonToast({
        text: "Download failed",
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      console.error("Download error:", error);

      return rejectWithValue(error.response?.data || error);
    }
  },
);

export const invoiceId = createAsyncThunk(
  "invoiceId",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/billing/${id}/invoice`,
        {
          validateStatus: () => true,
        },
      );

      if (response.status === 404) {
        return rejectWithValue(undefined);
      }

      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response.data);
      }

      return response.data;
    } catch (error) {
      const message = error?.response?.data?.error;

      if (message) {
        CommonToast({
          text: message,
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      }

      console.error("Error:", error);

      return rejectWithValue(error.response?.data || error);
    }
  },
);

const invoicesSlice = createSlice({
  name: "invoices",
  initialState: {
    invoiceData: [],
    isLoading: true,
    failData: null,

    specificData: [],
    specificLoader: true,
    specificFail: null,
  },
  extraReducers: (builder) => {
    builder.addCase(getInvoices.fulfilled, (state, action) => {
      state.invoiceData = action.payload;
      state.isLoading = false;
    });
    builder.addCase(getInvoices.rejected, (state, action) => {
      state.invoiceData = null;
      state.isLoading = false;
      state.failData = action.payload;
    });
    builder.addCase(getInvoices.pending, (state, action) => {
      state.isLoading = true;
    });

    builder.addCase(invoiceId.fulfilled, (state, action) => {
      state.specificData = action.payload;
      state.specificLoader = false;
    });
    builder.addCase(invoiceId.rejected, (state, action) => {
      state.specificData = null;
      state.specificLoader = false;
      state.specificFail = action.payload;
    });
    builder.addCase(invoiceId.pending, (state, action) => {
      state.specificLoader = true;
    });

    builder.addCase(downloadInvoice.pending, () => {
      CommonToast({
        text: "Download Started successfully.",
        icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        variant: "var(--success)",
        btnProps: {
          isDisabled: false,
          enabledTextColor: "var(--text-primary)",
          enabledBgColor: "var(--primary-bg)",
        },
      });
    });
    builder.addCase(downloadInvoice.rejected, (action) => {
      CommonToast({
        text: "Something went wrong",
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
    });
  },
});

export default invoicesSlice.reducer;
